import { useCallback, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import type { JiraBulkCreateResult } from '../../common/types';
import { createIssuesInBulk } from '../../utils/bulk-work-create';
import { getNumberIssuesFromDraftWorkItems } from '../../utils/getNumberIssuesFromDraftWorkItems';
import type { JiraContextData } from '../use-ai-create-project-context/types';

const messages = defineMessages({
	creationErrorCustomTitle: {
		id: 'jira-create-work-items.error-flag.custom-title',
		defaultMessage:
			"Couldn't create these work items due to {errorCount, plural, one {an error:} other {these errors:}}",
		description:
			'The title of the error flag when issue creation fails where the error count is provided.',
	},
	creationErrorDescription: {
		id: 'jira-create-work-items.error-flag.description',
		defaultMessage:
			"We're having some trouble creating these work items. Refresh the page and try again.",
		description: 'The description of the error flag when issue creation fails.',
	},
});

const CREATE_WORK_ITEMS_ACTION_KEY = 'jira-atlassian-work-item-create-all-field-types';

// Type predicate to safely check if an unknown value is a parent field with a string id.
// Needed because fields is Record<string, unknown> and fields.parent is unknown at runtime.
const isParentField = (value: unknown): value is { id: string } =>
	typeof value === 'object' &&
	value !== null &&
	'id' in value &&
	typeof (value as { id: unknown }).id === 'string';

// Util to convert a string to sentence case
const toSentenceCase = (str: string) => {
	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

type CreateIssuesSuccessResult = {
	isSuccess: true;
	createdIssueIds: string[];
	invocationIdToCreatedIssueKey: Record<string, string>;
	cloudUrl: string;
};

type CreateIssuesErrorResult = {
	isSuccess: false;
	errorMessage: string;
};

export const useCreateIssues = ({
	cloudId,
	isStagingArea,
	entryPoint,
	interactionModeExecuted,
}: {
	cloudId: string;
	isStagingArea: boolean;
	entryPoint?: string;
	interactionModeExecuted?: string;
}): {
	createIssues: ({
		draftWorkItems,
	}: {
		draftWorkItems: JiraContextData[];
	}) => Promise<CreateIssuesSuccessResult | CreateIssuesErrorResult>;
} => {
	const { formatMessage } = useIntl();
	const { trackAIMAUEvent } = useAnalytics();
	const experienceTracker = useAIMateExperienceTracker();
	const interactionModeExecutedAttributes = useMemo(
		() => ({
			...(interactionModeExecuted && { interactionModeExecuted }),
			...(entryPoint && { entryPoint }),
		}),
		[entryPoint, interactionModeExecuted],
	);

	const createIssues = useCallback(
		async ({
			draftWorkItems,
		}: {
			draftWorkItems: JiraContextData[];
		}): Promise<CreateIssuesSuccessResult | CreateIssuesErrorResult> => {
			// A single experience spans both the parent and child bulk-create requests.
			if (fg('jira-aifc-track-ufo-experiences')) {
				experienceTracker.start({
					id: ExperienceName.JIRA_CREATE_WORK_ITEMS_BULK,
					name: ExperienceName.JIRA_CREATE_WORK_ITEMS_BULK,
				});
			}

			// Customer-visible reliability experience for the Jira Rovo "create work
			// item" EXECUTION moment (RovoSkillsExecutionWorkItemCreate), feeding the
			// FY27 99.5% success-minutes KR (ATLAS-129636). Keyed to the real create
			// outcome the user sees. Both the in-chat create and the AIFC canvas /
			// staging-area create use this same skill, so both are tracked; the
			// `isStagingArea` attribute records which surface it came from so the two
			// can be sliced apart downstream.
			if (fg('rovo_skills_work_item_reliability_ufo')) {
				experienceTracker.start({
					id: ExperienceName.ROVO_SKILLS_EXECUTION_WORK_ITEM_CREATE,
					name: ExperienceName.ROVO_SKILLS_EXECUTION_WORK_ITEM_CREATE,
					attributes: { isStagingArea },
				});
			}

			const createdIssueKeys: string[] = [];
			const createdIssueIds: string[] = [];
			const errors: Set<string> = new Set();
			let cloudUrl: string = '';

			// Maintain a map of createdIssueKey to invocationId
			const invocationIdToCreatedIssueKey: Record<string, string> = {};

			// Create all the parent issues
			const parentSummaryToIssueIdMapping: Record<string, string | undefined> = {};

			const bulkParentResponse: JiraBulkCreateResult = await createIssuesInBulk({
				draftWorkItems,
				cloudId,
			});
			if (bulkParentResponse.isSuccess) {
				for (const [index, createdParent] of bulkParentResponse.createdIssues.entries()) {
					parentSummaryToIssueIdMapping[draftWorkItems[index].summary] = createdParent.id;
					createdIssueKeys.push(createdParent.key);
					createdIssueIds.push(createdParent.id);
					const invocationId = draftWorkItems[index].invocationId;
					invocationIdToCreatedIssueKey[invocationId] = createdParent.key;
					// The cloudUrl is the same for all created work items
					cloudUrl = bulkParentResponse.cloudUrl;
				}
			} else {
				bulkParentResponse.errorMessages.forEach((errorMessage) => errors.add(errorMessage));
			}

			// Create child issues, mapped to their created parent issues
			const childWorkItems = draftWorkItems
				.flatMap((workItem) => workItem.childWorkItems ?? [])
				.map((childWorkItem) => {
					const rawParent = childWorkItem.fields.parent;
					const parent = isParentField(rawParent) ? rawParent : undefined;
					const parentId = parent?.id
						? (parentSummaryToIssueIdMapping[parent.id] ?? undefined)
						: undefined;
					if (!parentId) {
						// if this is a subtask, then this will fail
						const { parent: _omitted, ...fieldsWithoutParent } = childWorkItem.fields;
						return { ...childWorkItem, fields: fieldsWithoutParent };
					}
					return {
						...childWorkItem,
						fields: { ...childWorkItem.fields, parent: { id: parentId } },
					};
				});

			if (childWorkItems.length !== 0) {
				const bulkChildrenResponse = await createIssuesInBulk({
					draftWorkItems: childWorkItems,
					cloudId,
				});
				if (bulkChildrenResponse.isSuccess) {
					for (const [index, createdChild] of bulkChildrenResponse.createdIssues.entries()) {
						createdIssueKeys.push(createdChild.key);
						createdIssueIds.push(createdChild.id);
						const invocationId = childWorkItems[index].invocationId;
						invocationIdToCreatedIssueKey[invocationId] = createdChild.key;
					}
				} else {
					bulkChildrenResponse.errorMessages.forEach((errorMessage) => errors.add(errorMessage));
				}
			}

			const totalCount = getNumberIssuesFromDraftWorkItems(draftWorkItems);
			if (totalCount === createdIssueKeys.length) {
				trackAIMAUEvent({
					type: 'actioned',
					actionResult: 'success',
					source: 'JiraAIFC',
					proactive: false,
					userGenerated: true,
					attributes: {
						...interactionModeExecutedAttributes,
						successCount: createdIssueKeys.length,
						failureCount: totalCount - createdIssueKeys.length,
						totalCount: totalCount,
						actionKey: CREATE_WORK_ITEMS_ACTION_KEY,
						isSingleWorkItem: totalCount === 1,
						isStagingArea,
					},
				});
				// track Rovo Assisted Work via object IDs
				trackAIMAUEvent({
					type: 'adopted',
					source: 'JiraAIFC',
					proactive: false,
					userGenerated: true,
					actionKey: CREATE_WORK_ITEMS_ACTION_KEY,
					rovoModifiedObjects: createdIssueIds?.map((id) => ({
						objectType: 'work-item',
						objectId: id,
					})),
					attributes: interactionModeExecutedAttributes,
				});
				// Handling resolving all the conversation actions and opening the Rovo chat should be handled by
				// the consumer of the button
				// Handle close and opening the Rovo chat should be handled by the onClick function provided
				if (fg('jira-aifc-track-ufo-experiences')) {
					experienceTracker.succeed({ name: ExperienceName.JIRA_CREATE_WORK_ITEMS_BULK });
				}

				if (fg('rovo_skills_work_item_reliability_ufo')) {
					experienceTracker.succeed({
						name: ExperienceName.ROVO_SKILLS_EXECUTION_WORK_ITEM_CREATE,
						attributes: { isSingleWorkItem: totalCount === 1, isStagingArea, totalCount },
					});
				}
			} else {
				// Fall back to default error message if there are no errors from the bulk create API
				const errorMessage =
					errors.size > 0
						? toSentenceCase(Array.from(errors).join(', '))
						: formatMessage(messages.creationErrorDescription);
				trackAIMAUEvent({
					type: 'actioned',
					actionResult: createdIssueKeys.length > 0 ? 'partial-failure' : 'failure',
					source: 'JiraAIFC',
					proactive: false,
					userGenerated: true,
					attributes: {
						...interactionModeExecutedAttributes,
						successCount: createdIssueKeys.length,
						failureCount: totalCount - createdIssueKeys.length,
						totalCount: totalCount,
						actionKey: CREATE_WORK_ITEMS_ACTION_KEY,
						isSingleWorkItem: totalCount === 1,
						isStagingArea,
					},
				});

				if (createdIssueIds.length > 0) {
					trackAIMAUEvent({
						type: 'adopted',
						source: 'JiraAIFC',
						proactive: false,
						userGenerated: true,
						actionKey: CREATE_WORK_ITEMS_ACTION_KEY,
						rovoModifiedObjects: createdIssueIds?.map((id) => ({
							objectType: 'work-item',
							objectId: id,
						})),
						attributes: interactionModeExecutedAttributes,
					});
				}

				trackAIMAUEvent({
					type: 'error',
					proactive: false,
					source: 'JiraAIFC',
					attributes: {
						totalCount: totalCount,
						successCount: createdIssueKeys.length,
						failureCount: totalCount - createdIssueKeys.length,
						actionKey: CREATE_WORK_ITEMS_ACTION_KEY,
						isSingleWorkItem: totalCount === 1,
						isStagingArea,
						...interactionModeExecutedAttributes,
						error: errorMessage,
					},
				});

				if (fg('jira-aifc-track-ufo-experiences')) {
					experienceTracker.fail({
						name: ExperienceName.JIRA_CREATE_WORK_ITEMS_BULK,
						error: new Error(errorMessage),
					});
				}

				if (fg('rovo_skills_work_item_reliability_ufo')) {
					experienceTracker.fail({
						name: ExperienceName.ROVO_SKILLS_EXECUTION_WORK_ITEM_CREATE,
						error: new Error(errorMessage),
						attributes: { isSingleWorkItem: totalCount === 1, isStagingArea, totalCount },
					});
				}

				return {
					isSuccess: false,
					errorMessage,
				};
			}

			return {
				isSuccess: true,
				createdIssueIds,
				invocationIdToCreatedIssueKey,
				cloudUrl,
			};
		},
		[
			cloudId,
			formatMessage,
			trackAIMAUEvent,
			experienceTracker,
			isStagingArea,
			interactionModeExecutedAttributes,
		],
	);
	return { createIssues };
};
